/**
 * ✅ PAKET SİPARİŞ FİŞİ YAZDIRMA SERVİSİ
 * Paket sipariş modunda müşteriye verilen sipariş fişlerini yazdırmak için kullanılır.
 * AdisyonPrintService.tsx baz alınarak oluşturulmuştur.
 */

import { printReceipt as sendPrintReceipt } from '../../../../services/localPrintService';
import { localServiceOrderFields } from '../utils/printedOrderNumber';

// Yerel yazıcı servisi endpoint (varsayılan, parametre ile override edilebilir)
const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';

/**
 * Uber Eats restoran destek hattı. Sipariş verisinde gelmediği için sabit
 * tutuluyor — Uber da kendi fişine bu numarayı sabit basıyor.
 */
export const UBER_EATS_SUPPORT_PHONE = '+90 850 210 7555';

// Paket fiş verisi için tip tanımlaması
export interface PackageReceiptItem {
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  vatRate?: number;
  subItems?: { name: string }[];
}

export interface PackageReceiptData {
  orderNumber: string;
  shortCode?: string;
  date: string;
  time: string;
  customerName: string;
  customerPhone?: string;
  customerAddress?: string;
  items: PackageReceiptItem[];
  subtotal: number;
  vatTotal: number;
  grandTotal: number;
  discountTotal?: number;
  paymentStatus?: string;
  paymentMethod?: string;
  isOnlinePaid?: boolean;
  orderNote?: string;
  companyInfo?: {
    name: string;
    address: string;
    taxNumber: string;
  };
  thankYouMessage?: string;
  standardFooterNote?: string;
  headerType?: 'text' | 'logo';
  logoUrl?: string;
  logoMaxWidthPx?: number;
  logoMaxHeightPx?: number;
  // Trendyol özel alanlar
  trendyolOrderCode?: string;
  callCenterPhone?: string;
  /**
   * Uber Eats (eski Trendyol Go) sipariş fişi. true ise yerel servis fişin orta
   * bölümünü Uber'in kendi fiş düzeninde basar: büyük "Sipariş Kodu", tarih,
   * müşteri/adres, "Açıklama", "Sipariş Notu" ve altta destek hattı + müşteri
   * iletişim blokları. Firma başlığı ve dipnot GTS'e özel kalır.
   */
  isUberEatsReceipt?: boolean;
  /** Restoran destek hattı telefonu (fişin alt bloğunda basılır). */
  supportPhone?: string;
  /** Destek hattında tuşlanacak restoran kodu (Uber supplierId). */
  supportCode?: string;
  // Platform bilgisi
  receiptLabel?: string;
  deliveryType?: string;
  customerNote?: string;
  addressDescription?: string;
  // İleri tarihli sipariş
  isPreOrder?: boolean;
  expectedDeliveryTime?: string;
  // Detaylı adres alanları
  mahalle?: string;
  cadde?: string;
  sokak?: string;
  apartman?: string;
  siteAdi?: string;
  blok?: string;
  binaNo?: string;
  kat?: string;
  daireNo?: string;
  /**
   * Adisyon fişine basılacak QR'ın görüntü data URL'i (data:image/png;base64,…).
   * Şu an Yemeksepeti siparişlerinde `delivery.address.latitude/longitude`
   * üzerinden üretilen Google Maps konum QR'ı için kullanılır. İleride başka
   * platformlar veya genel kullanım için de aynı alandan beslenebilir.
   */
  qrCodeDataUrl?: string;
  /** QR kutusunun üst başlığı (örn. "KURYE KONUM QR"). */
  qrCodeLabel?: string;
  /** QR'ın orijinal URL'i (debug / fallback metin için). */
  qrCodeRawUrl?: string;
}

/**
 * Paket sipariş fişi HTML şablonunu oluşturur
 */
export const createPackageReceiptHtml = (data: PackageReceiptData): string => {
  const items = data.items || [];
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Paket Sipariş Fişi</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 9pt;
          width: 80mm;
          margin: 0;
          padding: 2mm 4mm 3mm 4mm;
          box-sizing: border-box;
          line-height: 1.1;
        }
        .header {
          text-align: center;
          margin-bottom: 3px;
          padding-top: 0;
        }
        .header h2 {
          margin: 0;
          margin-bottom: 2px;
        }
        .header img {
          max-width: 50mm;
          max-height: 18mm;
          margin-left: -3mm;
          image-rendering: -webkit-optimize-contrast;
        }
        .company-info {
          text-align: center;
          margin-bottom: 3px;
          font-size: 8pt;
        }
        .company-info p {
          margin: 0 0 1px 0;
        }
        .customer-info {
          font-size: 10pt;
          margin: 5px 0;
          padding: 5px;
          border: 1px solid #ddd;
          background: #f9f9f9;
        }
        .customer-info p {
          margin: 2px 0;
        }
        .customer-name {
          font-weight: bold;
          font-size: 11pt;
        }
        .customer-address {
          font-size: 9pt;
          margin-top: 3px;
          padding: 3px;
          background: #fff;
          border: 1px dashed #ccc;
        }
        .order-number {
          font-size: 14pt;
          text-align: center;
          margin-bottom: 8px;
          margin-top: 5px;
          font-weight: bold;
          letter-spacing: 2px;
          color: #000;
        }
        .invoice-details {
          margin-bottom: 3px;
          font-size: 8pt;
        }
        .invoice-details p {
          margin: 0 0 1px 0;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
          margin-top: 2px;
          table-layout: fixed;
        }
        .items-table th, .items-table td {
          text-align: left;
          padding: 1px 0;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .items-table th {
          border-bottom: 1px solid #ddd;
        }
        .summary {
          margin-top: 3px;
          text-align: right;
          font-size: 8pt;
        }
        .summary p {
          margin: 0 0 1px 0;
        }
        .grand-total {
          font-size: 12pt;
          font-weight: bold;
          text-align: center;
          margin: 8px 0;
          padding: 5px;
          border: 2px solid #000;
          background: #f0f0f0;
        }
        .footer {
          margin-top: 5px;
          text-align: center;
          font-size: 8pt;
          border-top: 1px dashed #ddd;
          padding-top: 3px;
        }
        .footer p {
          margin: 0 0 1px 0;
        }
        .bold {
          font-weight: bold;
        }
        .center {
          text-align: center;
        }
        .right {
          text-align: right;
        }
        .receipt-label {
          font-size: 10pt;
          text-align: center;
          margin: 8px 0;
          font-weight: bold;
          border: 1px solid #ddd;
          padding: 3px;
          border-radius: 4px;
          background-color: #f9f9f9;
          letter-spacing: 2px;
        }
        .order-note {
          font-size: 8pt;
          margin: 5px 0;
          padding: 3px;
          border: 1px dashed #ff9800;
          background: #fff8e1;
        }
        .payment-status {
          text-align: center;
          font-size: 10pt;
          font-weight: bold;
          padding: 3px;
          margin: 5px 0;
          border-radius: 3px;
        }
        .payment-unpaid {
          background: #ffebee;
          color: #c62828;
          border: 1px solid #ef9a9a;
        }
        .payment-paid {
          background: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #a5d6a7;
        }
        .courier-code-box {
          margin: 6px 0;
          padding: 6px 5px;
          border: 2px solid #1a237e;
          background: #e8eaf6;
          text-align: center;
          border-radius: 4px;
        }
        .courier-code-label {
          font-size: 8pt;
          font-weight: bold;
          color: #1a237e;
          letter-spacing: 1px;
          display: block;
          margin-bottom: 2px;
        }
        .courier-code-number {
          font-size: 18pt;
          font-weight: bold;
          color: #1a237e;
          letter-spacing: 4px;
          display: block;
        }
        .courier-phone-line {
          font-size: 8pt;
          color: #333;
          margin-top: 3px;
          display: block;
        }
        .sub-item {
          font-size: 7.5pt;
          color: #555;
          padding-left: 8px;
        }
        .preorder-box {
          margin: 6px 0;
          padding: 8px 5px;
          border: 3px solid #000;
          background: #fff3e0;
          text-align: center;
        }
        .preorder-label {
          font-size: 12pt;
          font-weight: bold;
          letter-spacing: 2px;
          display: block;
          margin-bottom: 2px;
        }
        .preorder-time {
          font-size: 16pt;
          font-weight: bold;
          display: block;
        }
        .delivery-qr-box {
          margin: 8px 0;
          padding: 6px 4px;
          border: 2px solid #1976d2;
          background: #e3f2fd;
          text-align: center;
          border-radius: 4px;
        }
        .delivery-qr-label {
          font-size: 9pt;
          font-weight: bold;
          color: #0d47a1;
          margin-bottom: 4px;
          display: block;
          letter-spacing: 1px;
        }
        .delivery-qr-image {
          width: 38mm;
          height: 38mm;
          display: block;
          margin: 0 auto;
          image-rendering: pixelated;
        }
        .delivery-qr-hint {
          font-size: 7.5pt;
          color: #333;
          margin-top: 3px;
          display: block;
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${data.headerType === 'logo' && data.logoUrl ? 
          `<img src="${data.logoUrl}" alt="Logo" />` : 
          `<h2 style="margin: 0">${data.companyInfo?.name || 'AQUASOFT'}</h2>`
        }
      </div>
      
      ${data.headerType === 'logo' && data.logoUrl ? '' : `
      <div class="company-info">
        ${data.companyInfo?.address ? `<p>${data.companyInfo.address}</p>` : ''}
        ${data.companyInfo?.taxNumber ? `<p>VKN: ${data.companyInfo.taxNumber}</p>` : ''}
      </div>
      `}
      
      <div class="order-number">SİPARİŞ: ${(() => { const code = data.shortCode || data.orderNumber || ''; const m = code.match(/[DP]-\d+$/); return m ? m[0] : code; })()}</div>
      
      ${data.isPreOrder && data.expectedDeliveryTime ? `
      <div class="preorder-box">
        <span class="preorder-label">⏰ İLERİ TARİHLİ SİPARİŞ</span>
        <span class="preorder-time">TESLİM: ${(() => {
          const utcStr = data.expectedDeliveryTime!;
          const utcDate = utcStr.endsWith('Z') ? new Date(utcStr) : new Date(utcStr + 'Z');
          const trMs = utcDate.getTime() + 3 * 60 * 60 * 1000;
          const trDate = new Date(trMs);
          const h = String(trDate.getUTCHours()).padStart(2, '0');
          const m = String(trDate.getUTCMinutes()).padStart(2, '0');
          const d = String(trDate.getUTCDate()).padStart(2, '0');
          const mo = String(trDate.getUTCMonth() + 1).padStart(2, '0');
          return d + '.' + mo + ' ' + h + ':' + m;
        })()}</span>
      </div>
      ` : ''}
      
      <div class="customer-info">
        <p class="customer-name">${/[DP]-\d+$/.test(data.shortCode || data.orderNumber || '') ? '🖥️ Kiosk Müşterisi' : `📦 ${data.customerName}`}</p>
        ${/D-\d+$/.test(data.shortCode || data.orderNumber || '') ? '<p><strong>Self Servis</strong></p>' : /P-\d+$/.test(data.shortCode || data.orderNumber || '') ? '<p><strong>Paket Sipariş</strong></p>' : ''}
        ${data.customerPhone ? `<p>📱 ${data.customerPhone}</p>` : ''}
        ${data.customerAddress ? `<div class="customer-address">📍 ${data.customerAddress}</div>` : ''}
        ${(data.mahalle || data.cadde || data.sokak || data.apartman || data.siteAdi || data.blok || data.binaNo || data.kat || data.daireNo) ? `<div class="customer-address" style="font-size:11px;line-height:1.4;margin-top:2px;">${[data.mahalle ? `Mah: ${data.mahalle}` : '', data.cadde ? `Cad: ${data.cadde}` : '', data.sokak ? `Sok: ${data.sokak}` : '', data.apartman || data.siteAdi ? `${data.siteAdi || ''} ${data.apartman || ''}`.trim() : '', [data.blok ? `Blok: ${data.blok}` : '', data.binaNo ? `No: ${data.binaNo}` : '', data.kat ? `Kat: ${data.kat}` : '', data.daireNo ? `D: ${data.daireNo}` : ''].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</div>` : ''}
        ${data.addressDescription ? `<div class="order-note" style="margin-top:3px;border-color:#1976d2;background:#e3f2fd;"><b>📋 Adres Tarifi:</b> ${data.addressDescription}</div>` : ''}
      </div>
      
      <div class="invoice-details">
        <p><span class="bold">Tarih:</span> ${data.date} ${data.time}</p>
      </div>
      
      ${data.orderNote ? `<div class="order-note">📝 Not: ${data.orderNote}</div>` : ''}
      
      ${data.trendyolOrderCode ? `
      <div class="courier-code-box">
        <span class="courier-code-label">🔢 KURYE İLETİŞİM KODU</span>
        <span class="courier-code-number">${data.trendyolOrderCode}</span>
        ${data.callCenterPhone ? `<span class="courier-phone-line">📞 ${data.callCenterPhone} numarasını arayıp bu kodu tuşlayın</span>` : ''}
      </div>
      ` : ''}
      
      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 45%;">Ürün</th>
            <th class="right" style="width: 12%;">Adet</th>
            <th class="right" style="width: 20%;">Fiyat</th>
            <th class="right" style="width: 23%;">Tutar</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(item => `
            <tr>
              <td>${item.name}</td>
              <td class="right">${item.quantity}</td>
              <td class="right">${item.unitPrice.toFixed(2)}</td>
              <td class="right">${item.totalPrice.toFixed(2)}</td>
            </tr>
            ${item.description ? `
            <tr>
              <td colspan="4" class="sub-item" style="font-style:italic;color:#555;">${item.description}</td>
            </tr>
            ` : ''}
            ${(item.subItems || []).map(sub => `
            <tr>
              <td colspan="4" class="sub-item">${sub.name}</td>
            </tr>
            `).join('')}
          `).join('')}
        </tbody>
      </table>
      
      ${data.discountTotal && data.discountTotal > 0 ? `
      <div style="text-align:right; padding:4px 8px; font-size:12px; border-top:1px dashed #ccc; margin-top:4px;">
        Ara Toplam: ${(data.subtotal ?? (data.grandTotal + data.discountTotal)).toFixed(2)} TL
      </div>
      <div style="text-align:right; padding:4px 8px; font-size:12px; color:#c00; font-weight:bold;">
        İndirim: -${data.discountTotal.toFixed(2)} TL
      </div>
      ` : ''}
      <div class="grand-total">TOPLAM: ${data.grandTotal.toFixed(2)} TL</div>
      
      ${data.isOnlinePaid ? `
        <div class="payment-status payment-paid">
          💳 ONLİNE ÖDENDİ${data.paymentMethod ? ' (' + data.paymentMethod + ')' : ''}
        </div>
      ` : data.paymentStatus || /[DP]-\d+$/.test(data.shortCode || data.orderNumber || '') ? `
        <div class="payment-status ${data.paymentStatus === 'PAID' || /[DP]-\d+$/.test(data.shortCode || data.orderNumber || '') ? 'payment-paid' : 'payment-unpaid'}">
          ${data.paymentStatus === 'PAID' || /[DP]-\d+$/.test(data.shortCode || data.orderNumber || '') ? '✅ ÖDENDİ' : '⏳ ÖDEME BEKLENİYOR'}
        </div>
      ` : ''}
      
      <div class="receipt-label">${/D-\d+$/.test(data.shortCode || data.orderNumber || '') ? 'KİOSK SİPARİŞ FİŞİ - SELF SERVİS' : /P-\d+$/.test(data.shortCode || data.orderNumber || '') ? 'KİOSK SİPARİŞ FİŞİ - PAKET' : data.customerName?.includes('Masa Siparişi') ? 'MASA SİPARİŞ FİŞİ' : 'PAKET SİPARİŞ FİŞİ'}</div>
      
      ${data.qrCodeDataUrl ? `
      <div class="delivery-qr-box">
        <span class="delivery-qr-label">📍 ${data.qrCodeLabel || 'ADRES QR'}</span>
        <img class="delivery-qr-image" src="${data.qrCodeDataUrl}" alt="Konum QR" />
        <span class="delivery-qr-hint">Kameranızla okutun → Google Maps</span>
      </div>
      ` : ''}
      
      <div class="footer">
        ${data.standardFooterNote ? `<p>${data.standardFooterNote}</p>` : ''}
        ${data.thankYouMessage ? `<p>${data.thankYouMessage}</p>` : ''}
      </div>
    </body>
    </html>
  `;
};

/**
 * Paket sipariş fişini yazdırır
 */
export const printPackageReceipt = async (
  data: PackageReceiptData,
  printer: { id: string; name: string },
  localServiceUrl?: string
): Promise<void> => {
  const baseUrl = localServiceUrl || DEFAULT_LOCAL_SERVICE_URL;
  const htmlTemplate = createPackageReceiptHtml(data);
  
  console.log('🧾 [PAKET FİŞ YAZDIR] Fiş yazdırılıyor:', {
    printer: printer.name,
    customerName: data.customerName,
    orderNumber: data.shortCode || data.orderNumber,
    itemCount: data.items.length,
    total: data.grandTotal,
    serviceUrl: baseUrl
  });
  
  // Destek hattı sipariş verisinde gelmiyor; Uber Eats fişlerinde sabit numara
  // tek yerden enjekte edilir ki çağrı noktaları bunu tekrarlamasın.
  const enriched: PackageReceiptData = data.isUberEatsReceipt
    ? { ...data, supportPhone: data.supportPhone || UBER_EATS_SUPPORT_PHONE }
    : data;

  await sendPrintReceipt({
    printerId: printer.id,
    printerName: printer.name,
    receiptData: {
      ...enriched,
      // Yerel servis "SİPARİŞ" satırına orderNumber basar: yalnız kısa numara gönderilir,
      // "Fiş No" satırı basılmaz (telefon: PKT-12; uzun PKT-20261021-... fişe çıkmaz).
      // Uber Eats fişi kısa kodu kendisi basar, alttaki iletişim bloğunda uzun numara gerekir: dokunulmaz.
      ...(enriched.isUberEatsReceipt ? {} : localServiceOrderFields(enriched.orderNumber, enriched.shortCode)),
      htmlTemplate: htmlTemplate
    }
  }, { baseUrl });
  
  console.log('✅ [PAKET FİŞ YAZDIR] Fiş yazıcıya gönderildi');
};

export default {
  createPackageReceiptHtml,
  printPackageReceipt
};
